<script setup lang="ts">
import { ref } from "vue";
import SearchableModel from "../searchable-model/SearchableModel.vue";
import type { SearchableModelProps } from "@/components/searchable-model";
import ReviewItem from "@/components/reviews/ReviewItem.vue";
import useItemSelection from "@/composables/useItemSelection";
import { Button } from "@/shadcn/components/ui/button";
import { CloudUploadIcon } from "@lucide/vue";
import PublicationModal from "@/components/reviews/PublicationModal.vue";

const props = withDefaults(
  defineProps<SearchableModelProps & {
    publicationThirdPartyServices: any[]
  }>(),
  {
    availableFilters: () => [],
    publicationThirdPartyServices: () => [],
  }
);

const {
  selectedIds,
  isSelected,
  select,
  deselect,
  deselectAll,
  updateAllSelection,
  selectedCount,
} = useItemSelection();

const searchableModelRef = ref(null);

const handlePublished = () => {
  deselectAll();
  searchableModelRef.value?.getResults();
};

</script>

<template>
  <SearchableModel
    v-bind="{
      ...props,
      loadOnStart: true,
      modelName: 'ProductReview',
    }"
    ref="searchableModelRef"
  >
    <template #header-title>
      <slot name="header-title" />
    </template>
    <template #header-actions>
      <slot name="header-actions" />
    </template>
    <template #default="{ models }">
      <div class="tw:overflow-x-auto">
        <template v-if="selectedCount > 0">
          <div class="d-print-none tw:flex tw:items-center tw:gap-2 tw:mb-4">
            <span class="">
              {{ $t('Bulk actions:') }}
            </span>
            <PublicationModal
              :publication-third-party-services="publicationThirdPartyServices"
              :items="selectedIds"
              @published="handlePublished">
              <template #trigger>
                <Button variant="outline">
                  <CloudUploadIcon class="tw:w-3 tw:h-3" /> {{ $t('Publish on a third-party service') }}
                </Button>
              </template>
            </PublicationModal>
            <span class="tw:ml-auto tw:text-zinc-500">
              {{ $t("{count} reviews selected", {count: selectedCount}, selectedCount) }}
            </span>
          </div>
        </template>
        <table class="table">
          <thead class="tw:text-left tw:text-sm tw:bg-zinc-50 tw:hidden tw:md:table-header-group">
          <th v-if="publicationThirdPartyServices.length > 0" class="d-print-none tw:w-px tw:whitespace-nowrap">
            <input type="checkbox"
                   :checked="selectedCount === models?.length"
                   :indeterminate="selectedCount > 0 && selectedCount < models?.length"
                   @change="updateAllSelection($event.target.checked, models)" />
          </th>
          <th class="tw:hidden tw:sm:table-cell"></th>
          <th>{{ $t('Reviews') }}</th>
          <th>{{ $t('Status') }}</th>
          <th>{{ $t('Model') }}</th>
          <th>{{ $t('Author') }}</th>
          <th>{{ $t('Created at') }}</th>
          <th></th>
          </thead>
          <tbody>
          <tr v-for="model in models" :key="model.id">
            <td v-if="publicationThirdPartyServices.length > 0" class="d-print-none tw:w-px tw:whitespace-nowrap">
              <input type="checkbox"
                     :checked="isSelected(model.id)"
                     @change="$event.target.checked ? select(model.id) : deselect(model.id)" />
            </td>
            <ReviewItem :review="model"/>
          </tr>
          </tbody>
        </table>
      </div>
    </template>
  </SearchableModel>
</template>